/**
 * Versand-Countdown „Bestellt bis 12 Uhr – Versand heute!“ (snippets/dispatch-cutoff.liquid).
 *
 * Rechnet immer in Europe/Berlin (per Intl), unabhängig von der Zeitzone des Besuchers. Versandtage, Annahmeschluss,
 * bundesweite Feiertage und zusätzliche freie Tage kommen aus den Theme-Einstellungen
 * (JSON #DispatchCutoffConfig, snippets/dispatch-cutoff-config.liquid).
 *
 * Die reinen Datumsfunktionen sind exportiert und werden von tools/dispatch-cutoff.test.mjs geprüft.
 * Tage werden als „Tagesnummer“ gerechnet (Tage seit 1970-01-01, UTC) – so gibt es keine Sommerzeit-Fallen.
 */
export const TIME_ZONE = 'Europe/Berlin';
const DAY_MS = 86400000;

/** Kalenderdatum → Tagesnummer. */
export const toDayNumber = (y, m, d) => Math.floor(Date.UTC(y, m - 1, d) / DAY_MS);

/** Tagesnummer → {y, m, d}. */
export function fromDayNumber(n) {
  const date = new Date(n * DAY_MS);
  return { y: date.getUTCFullYear(), m: date.getUTCMonth() + 1, d: date.getUTCDate() };
}

/** ISO-Wochentag: 1 = Montag … 7 = Sonntag. */
export const isoWeekday = (n) => ((new Date(n * DAY_MS).getUTCDay() + 6) % 7) + 1;

/** Ostersonntag (gregorianisch, Gauß/Meeus-Algorithmus) als Tagesnummer. */
export function easterSunday(y) {
  const a = y % 19;
  const b = Math.floor(y / 100);
  const c = y % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return toDayNumber(y, month, day);
}

const holidayCache = new Map();

/**
 * Bundesweite gesetzliche Feiertage in Deutschland: Neujahr, Karfreitag, Ostermontag, Tag der Arbeit,
 * Christi Himmelfahrt, Pfingstmontag, Tag der Deutschen Einheit, 1. und 2. Weihnachtstag.
 * Landesfeiertage (z. B. Fronleichnam, Reformationstag) trägt der Shop bei Bedarf als freie Tage ein.
 */
export function germanHolidays(y) {
  if (!holidayCache.has(y)) {
    const easter = easterSunday(y);
    holidayCache.set(y, new Set([
      toDayNumber(y, 1, 1),
      easter - 2,
      easter + 1,
      toDayNumber(y, 5, 1),
      easter + 39,
      easter + 50,
      toDayNumber(y, 10, 3),
      toDayNumber(y, 12, 25),
      toDayNumber(y, 12, 26),
    ]));
  }
  return holidayCache.get(y);
}

export const isGermanHoliday = (n) => germanHolidays(fromDayNumber(n).y).has(n);

const DATE_TOKEN = /(\d{4})-(\d{1,2})-(\d{1,2})|(\d{1,2})\.(\d{1,2})(?:\.(\d{4}|\d{2}(?!\d))?)?/g;

const validDate = (y, m, d) => {
  const back = fromDayNumber(toDayNumber(y, m, d));
  return back.y === y && back.m === m && back.d === d;
};

/**
 * Liest die Liste freier Tage aus den Theme-Einstellungen. Ein Eintrag je Zeile (auch Komma/Semikolon):
 *   2026-12-24 | 24.12.2026 | 24.12. (jedes Jahr) | 22.12.2026–02.01.2027 | 24.12.–31.12. (jedes Jahr) | „bis“ als Trenner
 * Unlesbare Einträge werden ignoriert.
 * @returns {(n: number) => boolean} Prüffunktion für eine Tagesnummer
 */
export function parseClosedDays(text) {
  /** @type {{from: number, to: number, yearly: boolean}[]} */
  const rules = [];
  for (const entry of String(text ?? '').split(/[\n,;]+/)) {
    const tokens = [];
    for (const match of entry.matchAll(DATE_TOKEN)) {
      if (match[1]) tokens.push({ y: +match[1], m: +match[2], d: +match[3] });
      else {
        let y = match[6] ? +match[6] : null;
        if (y !== null && y < 100) y += 2000;
        tokens.push({ y, m: +match[5], d: +match[4] });
      }
      if (tokens.length === 2) break;
    }
    if (!tokens.length) continue;
    let [from, to] = tokens.length === 2 ? tokens : [tokens[0], { ...tokens[0] }];
    const key = (t) => t.m * 100 + t.d;
    if (from.y === null && to.y === null) {
      if (!validDate(2024, from.m, from.d) || !validDate(2024, to.m, to.d)) continue;
      rules.push({ from: key(from), to: key(to), yearly: true });
      continue;
    }
    // Fehlt nur bei einem Ende das Jahr, gilt das des anderen (über den Jahreswechsel hinweg passend)
    if (from.y === null) from = { ...from, y: key(from) > key(to) ? to.y - 1 : to.y };
    if (to.y === null) to = { ...to, y: key(to) < key(from) ? from.y + 1 : from.y };
    if (!validDate(from.y, from.m, from.d) || !validDate(to.y, to.m, to.d)) continue;
    const a = toDayNumber(from.y, from.m, from.d);
    const b = toDayNumber(to.y, to.m, to.d);
    rules.push({ from: Math.min(a, b), to: Math.max(a, b), yearly: false });
  }
  return (n) => {
    if (!rules.length) return false;
    const { m, d } = fromDayNumber(n);
    const md = m * 100 + d;
    return rules.some((r) => {
      if (!r.yearly) return n >= r.from && n <= r.to;
      return r.from <= r.to ? md >= r.from && md <= r.to : md >= r.from || md <= r.to;
    });
  };
}

const partsFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

/** Wanduhrzeit in Berlin: Tagesnummer + Sekunden seit Mitternacht. */
export function berlinClock(date) {
  const p = Object.fromEntries(partsFormatter.formatToParts(date).map((part) => [part.type, part.value]));
  return {
    day: toDayNumber(+p.year, +p.month, +p.day),
    seconds: (+p.hour % 24) * 3600 + +p.minute * 60 + +p.second,
  };
}

/** „12:00“ → 43200 (Sekunden seit Mitternacht); ungültig → 12 Uhr. */
export function parseCutoff(value) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value ?? '').trim());
  if (!match || +match[1] > 23 || +match[2] > 59) return 12 * 3600;
  return +match[1] * 3600 + +match[2] * 60;
}

/**
 * Normalisiert die Einstellungen.
 * @param {{cutoff?: string, days?: number[], holidays?: boolean, closed?: string, delivery?: {min: number, max: number}|null}} raw
 */
export function createConfig(raw = {}) {
  return {
    cutoff: parseCutoff(raw.cutoff),
    days: new Set((raw.days ?? [1, 2, 3, 4, 5]).map(Number)),
    holidays: raw.holidays !== false,
    isClosed: parseClosedDays(raw.closed),
    delivery: raw.delivery && raw.delivery.max > 0
      ? { min: Math.max(0, Number(raw.delivery.min) || 0), max: Math.max(Number(raw.delivery.min) || 0, Number(raw.delivery.max)) }
      : null,
  };
}

/** Versandtag laut Einstellungen (Wochentag, kein Feiertag, kein freier Tag)? */
export function isShipDay(n, config) {
  if (!config.days.has(isoWeekday(n))) return false;
  if (config.holidays && isGermanHoliday(n)) return false;
  return !config.isClosed(n);
}

/** Zustelltag des Paketdienstes: Mo–Fr ohne bundesweite Feiertage (vorsichtig, Samstag zählt nicht). */
const isDeliveryDay = (n) => isoWeekday(n) <= 5 && !isGermanHoliday(n);

function addDeliveryDays(start, count) {
  let n = start;
  for (let added = 0; added < count;) {
    n += 1;
    if (isDeliveryDay(n)) added += 1;
  }
  return n;
}

/**
 * Kernlogik: wann geht eine jetzt aufgegebene Bestellung raus?
 * @returns {null | {state: 'today'|'later', today: number, shipDay: number, daysAhead: number, secondsLeft: number,
 *   deliveryFrom: number|null, deliveryTo: number|null}} null, wenn in den nächsten 90 Tagen kein Versandtag liegt
 */
export function computeDispatch(now, config) {
  const { day: today, seconds } = berlinClock(now);
  let shipDay = null;
  let secondsLeft = 0;
  if (isShipDay(today, config) && seconds < config.cutoff) {
    shipDay = today;
    secondsLeft = config.cutoff - seconds;
  } else {
    for (let n = today + 1; n <= today + 90; n++) {
      if (isShipDay(n, config)) {
        shipDay = n;
        break;
      }
    }
  }
  if (shipDay === null) return null;
  return {
    state: shipDay === today ? 'today' : 'later',
    today,
    shipDay,
    daysAhead: shipDay - today,
    secondsLeft,
    deliveryFrom: config.delivery ? addDeliveryDays(shipDay, config.delivery.min) : null,
    deliveryTo: config.delivery ? addDeliveryDays(shipDay, config.delivery.max) : null,
  };
}

/** Restzeit → {h, m}; Minuten aufgerundet, damit „noch 0 Min.“ nie erscheint. */
export function splitRemaining(secondsLeft) {
  const minutes = Math.max(1, Math.ceil(secondsLeft / 60));
  return { h: Math.floor(minutes / 60), m: minutes % 60 };
}

/** Restzeit → Uhr {h, m, s} als zweistellige Texte (tickende Anzeige auf der Produktseite). */
export function clockParts(secondsLeft) {
  const total = Math.max(0, Math.floor(secondsLeft));
  const pad = (n) => String(n).padStart(2, '0');
  return { h: pad(Math.floor(total / 3600)), m: pad(Math.floor((total % 3600) / 60)), s: pad(total % 60) };
}

/** Letzte Stunde vor Annahmeschluss: Anzeige wird dringlicher. */
export const URGENT_SECONDS = 3600;

const fill = (template, values) => String(template ?? '').replace(/\{(\w+)\}/g, (_, key) => (key in values ? values[key] : `{${key}}`));

/**
 * Texte für einen Zustand (für Element und Tests).
 * @param {ReturnType<typeof computeDispatch>} result
 * @param {Record<string, string>} strings Übersetzungen aus der Konfiguration
 * @param {string} locale z. B. „de“, „en“
 */
export function describe(result, strings, locale = 'de') {
  if (!result) return null;
  const lang = locale.startsWith('en') ? 'en-GB' : locale;
  const at = (n, options) => new Intl.DateTimeFormat(lang, { timeZone: 'UTC', ...options }).format(new Date(n * DAY_MS));
  const dateShort = (n) => at(n, { day: '2-digit', month: '2-digit' }).replace(/\/$/, '');
  const weekdayShort = (n) => at(n, { weekday: 'short' }).replace(/\.$/, ''); // „Mo.“ (je nach ICU) → „Mo“
  const weekdayLong = (n) => at(n, { weekday: 'long' });

  let headline;
  let countdown = '';
  if (result.state === 'today') {
    headline = strings.today;
    const { h, m } = splitRemaining(result.secondsLeft);
    countdown = h > 0 ? fill(strings.countdown_hm, { h, m }) : fill(strings.countdown_m, { m });
  } else if (result.daysAhead === 1) {
    headline = fill(strings.tomorrow, { date: `${weekdayShort(result.shipDay)}, ${dateShort(result.shipDay)}` });
  } else {
    headline = fill(strings.later, { weekday: weekdayLong(result.shipDay), date: dateShort(result.shipDay) });
  }

  let delivery = '';
  if (result.deliveryFrom !== null && strings.delivery) {
    const range = result.deliveryFrom === result.deliveryTo
      ? `${weekdayShort(result.deliveryFrom)}, ${dateShort(result.deliveryFrom)}`
      : `${weekdayShort(result.deliveryFrom)}–${weekdayShort(result.deliveryTo)}`;
    delivery = fill(strings.delivery, { range });
  }
  return { headline, countdown, delivery };
}

// ---------------------------------------------------------------------------------------------------------------
// Web Component (nur im Browser)
// ---------------------------------------------------------------------------------------------------------------

let sharedConfig;

function readConfig() {
  if (sharedConfig !== undefined) return sharedConfig;
  sharedConfig = null;
  const node = document.getElementById('DispatchCutoffConfig');
  if (!node) return null;
  try {
    const raw = JSON.parse(node.textContent);
    sharedConfig = { ...raw, parsed: createConfig(raw) };
  } catch (error) {
    console.error(error);
  }
  return sharedConfig;
}

let clockOffset;

/**
 * Aktuelle Zeit. Nur lokal (localhost / 127.0.0.1, also Vorschau und `shopify theme dev`) lässt sich die Zeit
 * simulieren: URL-Parameter ?dispatch_now=2026-11-03T10:30:00+01:00 oder <html data-dispatch-now="…">.
 * Die simulierte Uhr läuft ab dann normal weiter.
 */
function now() {
  if (clockOffset === undefined) {
    clockOffset = 0;
    const local = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    const value = local && (new URLSearchParams(window.location.search).get('dispatch_now') || document.documentElement.dataset.dispatchNow);
    const simulated = value ? new Date(value) : null;
    if (simulated && !Number.isNaN(simulated.getTime())) clockOffset = simulated.getTime() - Date.now();
  }
  return new Date(Date.now() + clockOffset);
}

if (typeof window !== 'undefined' && typeof customElements !== 'undefined') {
  /**
   * <dispatch-cutoff>: ersetzt den statischen Text (ohne JS) durch den aktuellen Zustand und zählt die Restzeit
   * herunter. Vorgelesen wird nur ein Zustandswechsel (z. B. 12:00 → „Versand morgen“), nicht jede Minute.
   */
  class DispatchCutoff extends HTMLElement {
    #timer = 0;
    #key = '';

    connectedCallback() {
      const config = readConfig();
      if (!config) return;
      this.headline = this.querySelector('[data-dispatch-headline]');
      this.countdown = this.querySelector('[data-dispatch-countdown]');
      this.delivery = this.querySelector('[data-dispatch-delivery]');
      this.live = this.querySelector('[data-dispatch-live]');
      this.clock = this.querySelector('[data-dispatch-clock]');
      this.clockUnits = this.clock ? Object.fromEntries([...this.clock.querySelectorAll('[data-unit]')].map((el) => [el.dataset.unit, el])) : {};
      this.update();
      // mit Uhr sekündlich (nur geänderte Ziffern werden geschrieben), sonst alle 15 s
      this.#timer = window.setInterval(() => this.update(), this.clock ? 1000 : 15000);
      document.addEventListener('visibilitychange', this.#onVisible);
    }

    disconnectedCallback() {
      window.clearInterval(this.#timer);
      document.removeEventListener('visibilitychange', this.#onVisible);
    }

    #onVisible = () => {
      if (document.visibilityState === 'visible') this.update();
    };

    /** Für die Vorschau: Zeit neu setzen und sofort neu berechnen. */
    static resetClock() {
      clockOffset = undefined;
      for (const element of document.querySelectorAll('dispatch-cutoff')) element.update();
    }

    update() {
      const config = readConfig();
      if (!config) return;
      const result = computeDispatch(now(), config.parsed);
      const text = describe(result, config.strings ?? {}, config.locale || document.documentElement.lang || 'de');
      if (!text) {
        this.dataset.state = 'none';
        return;
      }
      this.dataset.state = result.state;
      this.toggleAttribute('data-urgent', result.state === 'today' && result.secondsLeft <= URGENT_SECONDS);
      if (this.clock) {
        this.clock.hidden = result.state !== 'today';
        const parts = clockParts(result.secondsLeft);
        for (const unit of ['h', 'm', 's']) {
          const el = this.clockUnits[unit];
          if (el && el.textContent !== parts[unit]) el.textContent = parts[unit];
        }
      }
      if (this.headline && this.headline.textContent !== text.headline) this.headline.textContent = text.headline;
      if (this.countdown) {
        if (this.countdown.textContent !== text.countdown) this.countdown.textContent = text.countdown;
        this.countdown.hidden = !text.countdown;
      }
      if (this.delivery) {
        this.delivery.textContent = text.delivery;
        this.delivery.hidden = !text.delivery;
      }
      const key = `${result.state}:${result.shipDay}`;
      if (this.#key && key !== this.#key && this.live) this.live.textContent = text.headline;
      this.#key = key;
    }
  }

  if (!customElements.get('dispatch-cutoff')) customElements.define('dispatch-cutoff', DispatchCutoff);
  window.DispatchCutoff = DispatchCutoff;
}
